import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { Plus, Trash2, PlusCircle, XCircle } from 'lucide-react';

function formatDate(ts) {
  const d = ts?.toDate?.();
  if (!d) return '';
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function ReleaseNotesManager() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', items: [''] });
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'releaseNotes'), orderBy('publishedAt', 'desc')));
      setNotes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => setForm({ title: '', items: [''] });

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    const items = form.items.map(s => s.trim()).filter(Boolean);
    if (!form.title.trim()) return setError('Title is required.');
    if (!items.length) return setError('At least one bullet point is required.');
    setSaving(true);
    try {
      await addDoc(collection(db, 'releaseNotes'), {
        title: form.title.trim(),
        publishedAt: serverTimestamp(),
        items,
      });
      setShowForm(false);
      resetForm();
      await load();
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    await deleteDoc(doc(db, 'releaseNotes', id));
    setNotes(prev => prev.filter(n => n.id !== id));
  };

  const updateItem = (i, val) =>
    setForm(f => { const items = [...f.items]; items[i] = val; return { ...f, items }; });

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, ''] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-700">
          Publish updates shown to all users on login. Write from the user's perspective — what changed, not how.
        </p>
        <button
          onClick={() => { resetForm(); setShowForm(true); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shrink-0 ml-4"
        >
          <Plus size={15} aria-hidden="true" />
          New update
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6 space-y-4">
          <h3 className="text-sm font-semibold text-gray-800">New release note</h3>
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="rn-title">Title</label>
            <input
              id="rn-title"
              type="text"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. May 2026 — New reporting features"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
            />
          </div>

          <div>
            <p className="text-xs font-medium text-gray-700 mb-2">Bullet points</p>
            <div className="space-y-2">
              {form.items.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={e => updateItem(i, e.target.value)}
                    placeholder={`Point ${i + 1}`}
                    aria-label={`Bullet point ${i + 1}`}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
                  />
                  {form.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(i)}
                      aria-label={`Remove point ${i + 1}`}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <XCircle size={16} aria-hidden="true" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addItem}
              className="mt-2 flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"
            >
              <PlusCircle size={14} aria-hidden="true" />
              Add point
            </button>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving…' : 'Publish note'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-gray-600 py-8 text-center">Loading…</p>
      ) : notes.length === 0 ? (
        <p className="text-sm text-gray-600 py-8 text-center">No release notes published yet.</p>
      ) : (
        <div className="space-y-4">
          {notes.map(note => (
            <div key={note.id} className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{note.title}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{formatDate(note.publishedAt)}</p>
                </div>
                <button
                  onClick={() => handleDelete(note.id, note.title)}
                  aria-label={`Delete note: ${note.title}`}
                  className="p-1.5 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                >
                  <Trash2 size={15} aria-hidden="true" />
                </button>
              </div>
              <ul className="space-y-1 ml-1">
                {(note.items || []).map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-blue-500 mt-1 shrink-0" aria-hidden="true">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
